import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculatorAPI, tariffsAPI, servicesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calculator as CalculatorIcon, 
  Plus, 
  Trash2, 
  ArrowRight,
  Loader2,
  Info,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Plastic packaging materials only for Zambia
const MATERIAL_TYPES = [
  'PET - transparent/light',
  'PET - dark colors',
  'HDPE',
  'PP transparent',
  'LDPE - transparent/white',
  'LDPE - other colours'
];
// 2026 only
const YEARS = [2026];

// USD to ZMW conversion rate (1 USD = 20 ZMW)
const USD_TO_ZMW = 20;

export default function Calculator() {
  const navigate = useNavigate();
  // Country is fixed to Zambia
  const selectedCountry = 'Zambia';
  const [selectedYear, setSelectedYear] = useState(2026);
  const [materials, setMaterials] = useState([
    { material_type: 'PET - transparent/light', quantity_tonnes: 0 }
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculatorService, setCalculatorService] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const servicesRes = await servicesAPI.getAll();
      // Find calculator-based service
      const calcService = servicesRes.data.find(s => s.is_calculator_based);
      setCalculatorService(calcService);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const addMaterial = () => {
    const usedTypes = materials.map(m => m.material_type);
    const availableTypes = MATERIAL_TYPES.filter(t => !usedTypes.includes(t));
    
    if (availableTypes.length === 0) {
      toast.error('All material types have been added');
      return;
    }
    
    setMaterials([
      ...materials,
      { material_type: availableTypes[0], quantity_tonnes: 0 }
    ]);
  };

  const removeMaterial = (index) => {
    if (materials.length === 1) {
      toast.error('At least one material is required');
      return;
    }
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index][field] = field === 'quantity_tonnes' ? parseFloat(value) || 0 : value;
    setMaterials(updated);
    setResult(null); // Clear result when inputs change
  };

  const calculateFees = async () => {
    const validMaterials = materials.filter(m => m.quantity_tonnes > 0);
    if (validMaterials.length === 0) {
      toast.error('Please enter at least one material quantity');
      return;
    }

    setLoading(true);
    try {
      const response = await calculatorAPI.calculate({
        country: selectedCountry,
        year: selectedYear,
        materials: validMaterials
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate fees');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    if (!result) return;
    
    navigate('/checkout', {
      state: {
        service: calculatorService,
        calculatorData: {
          ...result,
          country: selectedCountry,
          year: selectedYear,
          materials: materials.filter(m => m.quantity_tonnes > 0)
        }
      }
    });
  };

  const formatCurrencyUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="min-h-screen" data-testid="calculator-page">
      <SEO 
        title="EPR Fee Calculator | AfriCycle Zambia Pricing Tool"
        description="Calculate your EPR compliance fees for plastic packaging and WEEE waste collection in Zambia. Get instant estimates based on material types and volumes with AfriCycle Zambia's online calculator."
        keywords="EPR fee calculator Zambia, plastic packaging fees calculator, WEEE fees Zambia, waste collection pricing, AfriCycle calculator, environmental compliance costs"
        canonicalUrl="https://africycle.pro/calculator"
      />
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D2E4D6] text-[#056839] mb-6">
              <CalculatorIcon className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#056839] mb-6">
              FEE CALCULATOR
            </h1>
            <p className="text-sm text-[#6B7280] mt-4">
              <strong>Note:</strong> This calculator covers waste collection & treatment services only. 
              Regulatory and compliance consulting services are available separately through our Compliance Packages.
            </p>
          </div>

          {/* Disclaimer Banner */}
          <div className="max-w-3xl mx-auto mt-8">
            <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-xl p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-[#D97706] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#92400E]">
                <span className="font-semibold">Important:</span> These fees are indicative only. Final fees will be confirmed and agreed upon on your invoice based on the prevailing exchange rates and your actual declared volumes.
              </div>
            </div>
          </div>

          {/* WEEE Coming Soon */}
          <div className="max-w-3xl mx-auto mt-4">
            <div className="bg-[#EDE9FE] border border-[#8B5CF6] rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#7C3AED] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#5B21B6]">
                <span className="font-semibold">Coming Soon:</span> Fees for WEEE (Waste Electrical and Electronic Equipment) collection and treatment assurance will be available soon.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12 bg-[#D2E4D6]" data-testid="calculator-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 animate-slide-up">
            {/* Country & Year Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <Label className="text-[#3F3F3F] font-medium mb-2 block">Country</Label>
                <div className="h-12 px-4 border rounded-md bg-[#D2E4D6]/30 flex items-center">
                  <span className="text-[#056839] font-medium">Zambia</span>
                </div>
                <p className="text-xs text-[#6B7280] mt-1">AfriCycle Zambia operates exclusively in Zambia</p>
              </div>
              
              <div>
                <Label className="text-[#3F3F3F] font-medium mb-2 block">Reporting Year</Label>
                <Select 
                  value={selectedYear.toString()} 
                  onValueChange={(v) => setSelectedYear(parseInt(v))}
                >
                  <SelectTrigger className="h-12" data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Material Inputs */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-[#3F3F3F] font-medium">Plastic Packaging Material Quantities (tonnes)</Label>
                <Button 
                  type="button"
                  variant="ghost" 
                  onClick={addMaterial}
                  className="text-[#056839] hover:bg-[#D2E4D6]/50"
                  disabled={materials.length >= MATERIAL_TYPES.length}
                  data-testid="add-material-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Material
                </Button>
              </div>

              <div className="space-y-4">
                {materials.map((material, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-4 bg-[#F9FAFB] rounded-lg"
                    data-testid={`material-row-${index}`}
                  >
                    <div className="flex-1">
                      <Select 
                        value={material.material_type}
                        onValueChange={(v) => updateMaterial(index, 'material_type', v)}
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MATERIAL_TYPES.map(type => (
                            <SelectItem 
                              key={type} 
                              value={type}
                              disabled={materials.some((m, i) => i !== index && m.material_type === type)}
                            >
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="Quantity (tonnes)"
                        value={material.quantity_tonnes || ''}
                        onChange={(e) => updateMaterial(index, 'quantity_tonnes', e.target.value)}
                        className="h-12"
                        min="0"
                        step="0.01"
                        data-testid={`material-quantity-${index}`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeMaterial(index)}
                      className="text-red-500 hover:bg-red-50"
                      disabled={materials.length === 1}
                      data-testid={`remove-material-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculate Button */}
            <Button 
              onClick={calculateFees}
              className="w-full btn-primary h-14 text-lg"
              disabled={loading}
              data-testid="calculate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <CalculatorIcon className="mr-2 h-5 w-5" />
                  Calculate Fees
                </>
              )}
            </Button>

            {/* Results */}
            {result && (
              <div className="mt-8 p-6 bg-[#D2E4D6]/30 rounded-xl animate-fade-in" data-testid="calculator-result">
                <h3 className="text-xl font-semibold text-[#056839] mb-4">FEE BREAKDOWN</h3>
                
                {/* Items */}
                <div className="space-y-3 mb-6">
                  {result.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div>
                        <span className="text-[#3F3F3F] font-medium">{item.material_type}</span>
                        <span className="text-[#6B7280] text-sm ml-2">
                          ({item.quantity_tonnes.toLocaleString()} tonnes × {formatCurrencyUSD(item.rate_per_tonne)}/tonne)
                        </span>
                      </div>
                      <span className="font-medium text-[#3F3F3F]">{formatCurrencyUSD(item.cost)}</span>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-2 pt-4 border-t border-gray-300">
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Subtotal</span>
                    <span className="text-[#3F3F3F]">{formatCurrencyUSD(result.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">VAT ({result.country})</span>
                    <span className="text-[#3F3F3F]">{formatCurrencyUSD(result.admin_fee)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="text-xl font-semibold text-[#056839]">Total</span>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-[#056839]">{formatCurrencyUSD(result.total)}</span>
                      <span className="text-[#A0522D] text-sm block">
                        ~ZMW {(result.total * USD_TO_ZMW).toLocaleString()}*
                      </span>
                    </div>
                  </div>
                </div>

                {/* Purchase Button */}
                <Button 
                  onClick={handlePurchase}
                  className="w-full btn-primary h-14 text-lg mt-6"
                  data-testid="purchase-calculated-btn"
                >
                  Request This Service
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                {/* Info */}
                <div className="flex items-start gap-2 mt-4 text-sm text-[#6B7280]">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    This is an estimate based on current tariff rates for {result.country} ({result.year}). 
                    Final fees will be confirmed and agreed upon on your invoice.
                  </p>
                </div>

                {/* ZMW Disclaimer */}
                <div className="flex items-start gap-2 mt-2 text-sm text-[#A0522D]">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    *ZMW amount is indicative only. The actual ZMW amount will be calculated using the prevailing exchange rate at the time of invoicing.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
